import { useState } from "react";
import { Mail, MapPin, Linkedin, Instagram, Calendar, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, waLink, DEFAULT_WA_MSG } from "@/config";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Reveal from "@/components/Reveal";

const SERVICE_OPTIONS = [
    "Website Design & Development",
    "Data Analytics & Data Cleaning",
    "Excel Reports & Dashboards",
    "Power BI Dashboards",
    "AI Video & Advertisement Creation",
    "Logo Design & Branding",
    "Social Media Content Creation",
    "Digital Marketing",
    "Other Services",
];
const CONTACT_METHODS = ["WhatsApp Message", "WhatsApp Call", "Phone Call", "Email"];
const BEST_TIMES = ["Morning", "Afternoon", "Evening", "Any Time"];

const INFO = [
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "contact-email" },
    { icon: MapPin, label: "Location", value: CONTACT.location, href: null, id: "contact-location" },
    { icon: WhatsAppIcon, label: "WhatsApp", value: CONTACT.phoneDisplay, href: waLink(DEFAULT_WA_MSG), id: "contact-whatsapp" },
    { icon: Linkedin, label: "LinkedIn", value: "linkedin.com/in/analytrova-group-230976437", href: CONTACT.linkedin, id: "contact-linkedin" },
    { icon: Instagram, label: "Instagram", value: "instagram.com/analytrova", href: CONTACT.instagram, id: "contact-instagram" },
    { icon: Calendar, label: "Booking", value: "Book Your 30-Minute Assessment", href: "#book", id: "contact-booking" },
];

export default function Contact() {
    const EMPTY_FORM = { name: "", business: "", phone: "", whatsapp_number: "", email: "", city: "", service: "", project_description: "", preferred_contact: "", best_time: "" };
    const [form, setForm] = useState(EMPTY_FORM);

    const buildWaMessage = (f) =>
        [
            "New Enquiry from the Analytrova website:",
            f.name && `Full Name: ${f.name}`,
            f.business && `Business Name: ${f.business}`,
            f.phone && `Mobile Number: ${f.phone}`,
            f.whatsapp_number && `WhatsApp Number: ${f.whatsapp_number}`,
            f.email && `Email: ${f.email}`,
            f.city && `City: ${f.city}`,
            f.service && `Service Required: ${f.service}`,
            f.preferred_contact && `Preferred Contact Method: ${f.preferred_contact}`,
            f.best_time && `Best Time to Contact: ${f.best_time}`,
            f.project_description && `Project Description: ${f.project_description}`,
        ]
            .filter(Boolean)
            .join("\n");
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, form);
            toast.success(data.email_sent === false
                ? "Thank you! Your enquiry has been received — we'll get back to you soon."
                : "Thank you! Your enquiry has been received and emailed to our team — we'll get back to you soon.");
            window.open(waLink(buildWaMessage(form)), "_blank");
            setForm(EMPTY_FORM);
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again or message us on WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="py-20 lg:py-28" data-testid="contact-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
                {/* Left — info */}
                <Reveal>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="h-px w-10 bg-gold-deep" />
                        <span className="text-[11px] font-semibold tracking-[0.34em] text-burgundy">GET IN TOUCH</span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl text-charcoal">Let's Work Together</h2>
                    <p className="mt-5 text-charcoal/70 max-w-md leading-relaxed">
                        From data analytics to website design and creative branding — let's bring your business ideas to life.
                        Have a project, business idea or data challenge? Let's discuss how Analytrova can help — we respond within 24 hours.
                    </p>

                    <ul className="mt-10 space-y-4">
                        {INFO.map(({ icon: Icon, label, value, href, id }) => (
                            <li key={label} className="flex items-center gap-4">
                                <span className="w-11 h-11 rounded-full bg-burgundy/8 border border-burgundy/12 text-burgundy flex items-center justify-center shrink-0">
                                    <Icon className="w-4.5 h-4.5 w-5 h-5" strokeWidth={1.6} />
                                </span>
                                <div>
                                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-olive-deep">{label}</div>
                                    {href ? (
                                        <a
                                            href={href}
                                            target={href.startsWith("http") ? "_blank" : undefined}
                                            rel="noopener noreferrer"
                                            className="text-[15px] text-charcoal hover:text-burgundy transition-colors"
                                            data-testid={id}
                                        >
                                            {value}
                                        </a>
                                    ) : (
                                        <div className="text-[15px] text-charcoal" data-testid={id}>{value}</div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    <a
                        href={waLink(DEFAULT_WA_MSG)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-burgundy mt-9 group"
                        data-testid="contact-whatsapp-main"
                    >
                        <WhatsAppIcon className="w-4 h-4" />
                        CONTACT US ON WHATSAPP
                    </a>
                </Reveal>

                {/* Right — form */}
                <Reveal delay={0.1}>
                    <form
                        onSubmit={submit}
                        className="bg-cream-soft border border-burgundy/12 rounded-2xl p-7 sm:p-9 shadow-[0_30px_60px_-40px_rgba(90,0,16,0.3)]"
                        data-testid="contact-form"
                    >
                        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-olive-deep mb-5">Customer Contact Details</p>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="field-label" htmlFor="cf-name">Full Name</label>
                                <input id="cf-name" className="field" placeholder="Your full name" value={form.name} onChange={set("name")} required data-testid="contact-name" />
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-business">Business Name / Company Name</label>
                                <input id="cf-business" className="field" placeholder="Your business name" value={form.business} onChange={set("business")} data-testid="contact-business" />
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-phone">Mobile Number</label>
                                <input id="cf-phone" type="tel" className="field" placeholder="+91 ..." value={form.phone} onChange={set("phone")} required data-testid="contact-phone-input" />
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-whatsapp">WhatsApp Number (if different)</label>
                                <input id="cf-whatsapp" type="tel" className="field" placeholder="Optional" value={form.whatsapp_number} onChange={set("whatsapp_number")} data-testid="contact-whatsapp-number" />
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-email">Email Address</label>
                                <input id="cf-email" type="email" className="field" placeholder="you@example.com" value={form.email} onChange={set("email")} required data-testid="contact-email-input" />
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-city">City / Location</label>
                                <input id="cf-city" className="field" placeholder="Your city" value={form.city} onChange={set("city")} data-testid="contact-city" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="field-label" htmlFor="cf-service">Service Required</label>
                                <select id="cf-service" className="field" value={form.service} onChange={set("service")} required data-testid="contact-service">
                                    <option value="" disabled>Select a service</option>
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="field-label" htmlFor="cf-method">Preferred Contact Method</label>
                                <select id="cf-method" className="field" value={form.preferred_contact} onChange={set("preferred_contact")} data-testid="contact-preferred">
                                    <option value="" disabled>Select method</option>
                                    {CONTACT_METHODS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="field-label" htmlFor="cf-best">Best Time to Contact</label>
                                <div className="flex flex-wrap gap-2">
                                    {BEST_TIMES.map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setForm({ ...form, best_time: form.best_time === t ? "" : t })}
                                            className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
                                                form.best_time === t
                                                    ? "bg-burgundy text-cream border-burgundy"
                                                    : "text-burgundy border-burgundy/25 hover:border-burgundy"
                                            }`}
                                            data-testid={`contact-best-${t.toLowerCase().replace(" ", "-")}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="field-label" htmlFor="cf-message">Project Description</label>
                            <textarea id="cf-message" rows="4" className="field resize-none" placeholder="Tell us about your project, goals and requirements..." value={form.project_description} onChange={set("project_description")} required data-testid="contact-message" />
                        </div>
                        <div className="mt-7">
                            <button type="submit" className="btn btn-burgundy justify-center w-full disabled:opacity-70" disabled={sending} data-testid="contact-submit">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="btn-arrow w-4 h-4" />}
                                {sending ? "Sending..." : "Send Enquiry"}
                            </button>
                        </div>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
